import { useMemo } from 'react';
import type { SEOConfig } from '@/components/seo';
import type { User, Commit } from '../types/api';

const BASE_URL = 'https://commit.you';
const MEDIA_URL = 'https://media.commit.you';

// Feed page SEO
export function useFeedSEO(): SEOConfig {
  return useMemo(() => ({
    title: 'Developer Updates',
    description: 'See what developers are working on. Follow your favorite engineers and discover new commits.',
    canonical: BASE_URL,
    type: 'website' as const,
  }), []);
}

// Profile page SEO
export function useProfileSEO(user: User | null, branch?: string | null): SEOConfig {
  return useMemo(() => {
    if (!user) {
      return {
        title: 'Profile',
        description: 'Developer profile on commit.you',
        canonical: BASE_URL,
        noIndex: true,
      };
    }

    const displayName = user.displayName || user.username;
    const title = branch
      ? `${branch} - ${displayName}`
      : `${displayName} (@${user.username})`;

    const description = user.bio
      ? user.bio.slice(0, 150) + (user.bio.length > 150 ? '...' : '')
      : `${displayName}'s commits on commit.you. ${user.commitCount || 0} commits, ${user.followerCount || 0} followers.`;

    const canonical = `https://${user.username}.commit.you${branch ? `/${branch}` : ''}`;

    // Use dynamically generated profile OG image
    const ogImage = `${MEDIA_URL}/og-images/profiles/${user.id}.png`;

    return {
      title,
      description,
      canonical,
      image: ogImage,
      type: 'profile' as const,
    };
  }, [user, branch]);
}

// Commit detail page SEO
export function useCommitSEO(commit: Commit | null): SEOConfig {
  return useMemo(() => {
    if (!commit) {
      return {
        title: 'Commit',
        description: 'A commit on commit.you',
        canonical: BASE_URL,
        noIndex: true,
      };
    }

    const author = commit.user;
    const authorUsername = author?.username || 'unknown';

    // Build full commit title with type prefix
    const fullMessage = commit.type
      ? `${commit.type}: ${commit.message}`
      : commit.message;

    // Truncate for title
    const truncatedMessage = fullMessage.length > 60
      ? `${fullMessage.slice(0, 60)}...`
      : fullMessage;

    const title = `${truncatedMessage} by @${authorUsername}`;

    // Build description
    let description = fullMessage;
    if (commit.body) {
      description += ` - ${commit.body.slice(0, 100)}`;
    }
    if (commit.tags && commit.tags.length > 0) {
      description += ` Tags: ${commit.tags.map(t => `#${t}`).join(', ')}`;
    }
    description = description.slice(0, 160);

    // Use generated OG image for commit
    const ogImage = `${MEDIA_URL}/og-images/commits/${commit.id}.png`;

    return {
      title,
      description,
      canonical: `${BASE_URL}/c/${commit.id}`,
      image: ogImage,
      type: 'article' as const,
    };
  }, [commit]);
}

// Tag page SEO
export function useTagSEO(tag: string | undefined, commitCount?: number): SEOConfig {
  return useMemo(() => {
    if (!tag) {
      return {
        title: 'Tags',
        description: 'Explore tagged commits on commit.you',
        canonical: BASE_URL,
        noIndex: true,
      };
    }

    const description = commitCount !== undefined
      ? `Explore ${commitCount} commit${commitCount !== 1 ? 's' : ''} tagged with #${tag} on commit.you`
      : `Explore commits tagged with #${tag} on commit.you`;

    return {
      title: `#${tag}`,
      description,
      canonical: `${BASE_URL}/tag/${tag}`,
      type: 'website' as const,
    };
  }, [tag, commitCount]);
}
